<template>
  <div class="image-editor" id="main-editor" @click="onImageEditorClicked">
    <!-- {{ drawingPointerStyle }}=={{ drawingCursorPosition }} -->
    <PanZoom>
      <a-spin :spinning="appState.inpainting">
        <div class="xj-canvas relative" @mouseover="onMouseOver"
          :style="{ transform: `scale(${appState.canvasScale})` }" @click.stop>
          <canvas id="xj-canvas" :width="appState.editorSize.canvasWidth"
            :height="appState.editorSize.canvasHeight"></canvas>
          <!-- <Watermarker v-if="appState.editorSize.canvasWidth" /> -->
          <div class="grab-placeholder" v-show="appState.isSpaceBarPressed"
            :class="{ grabing: appState.isCanvasGrabing }"></div>
          <canvas id="inpaint-mask" class="inpaint-mask" v-show="appState.isInpaintMode"
            :width="appState.editorSize.canvasWidth" :height="appState.editorSize.canvasHeight"></canvas>
          <div class="pointer-canvas" :style="drawingPointerStyle" :class="{ 'inpaint-mode': appState.isInpaintMode }">
          </div>
        </div>
      </a-spin>
      <div v-show="appState.isColorPicking" class="abs-full color-picking-mask" @click.stop></div>
    </PanZoom>
  </div>
</template>
<script setup>
import { computed, watch, onMounted, nextTick } from 'vue';
import Watermarker from '../Watermarker.vue';
import PanZoom from '../PanZoom.vue';
import useSize from '../useSize';
import { initCenteringGuidelines, initAligningGuidelines } from '@/libs/fabric-lib';
import { useStore } from 'vuex';
import { fabric } from 'fabric';
import { isEqual, debounce } from 'lodash-es';
import useKeyboard from '@/compositions/useKeyboard';
import { getGroupingObjects, getCORSImageUrl, activeElementNotInputable } from '@/utils';
import apis from '@/apis';
import useController from './useController';
import { sendLog } from '@/utils/sentry';
import useDrawing from '@/compositions/useDrawing';
import { useRoute } from 'vue-router';
import editorCore from '@/core';

const emit = defineEmits(['drawingPointerStyleChange']);
const store = useStore();
const route = useRoute();

const appState = computed(() => store.state.app);
const multiState = computed(() => store.state.multi);
const isUndoRedoRendering = computed(() => appState.value.isUndoRedoRendering);
const requestId = computed(() => store.state.multi.requestId);
const againRequestId = computed(() => store.state.multi.againRequestId);
const selectedObjectProps = computed(() => store.getters['app/selectedObjectProps']);

const { drawingCursorPosition, onMouseOut, onMouseOver, onCanvasMouseMove, onCanvasMouseUp } = useDrawing();

const drawingPointerStyle = computed(() => {
  const { isDrawingMode, drawingConfig = {}, isColorPicking, inpaintCursorPosition, isInpaintMode } = appState.value;
  let { show, x, y } = drawingCursorPosition.value;
  if (isInpaintMode) {
    show = inpaintCursorPosition.show;
    x = inpaintCursorPosition.x;
    y = inpaintCursorPosition.y;
  }
  const size = drawingConfig.brushWidth;
  return {
    display: isDrawingMode && show && !isColorPicking ? 'block' : 'none',
    left: x - size / 2 + 'px',
    top: y - size / 2 + 'px',
    width: size + 'px',
    height: size + 'px',
    backgroundColor: drawingConfig.brushColor,
  };
});

watch(
  drawingPointerStyle,
  nv => {
    emit('drawingPointerStyleChange', nv);
  },
  { immediate: true }
);

onMounted(() => {
  watch(
    [requestId, againRequestId],
    ([newRequestId, newAgainRequestId], [oldRequestId, oldAgainRequestId]) => {
      // debugger
      // (newRequestId || newAgainRequestId) &&
      //   (oldRequestId !== newRequestId || newAgainRequestId !== oldAgainRequestId) &&
      //   init();
      init();
    },
    { immediate: true }
  );
});

watch(isUndoRedoRendering, (nv, ov) => {
  if (nv) {
    const { objectPropsMap, objectUIDs, cropInfo } = appState.value;
    store.dispatch('app/renderAll', { objectPropsMap, objectUIDs, cropInfo });
  }
});

watch(selectedObjectProps, (nv, ov) => {
  if (ov && !isEqual(nv, ov) && appState.value.selectedObjectUid > -1) {
    console.log('app/updateSelectedObjectFromPropsEditor', nv);
    store.dispatch('app/updateSelectedObjectFromPropsEditor');
  }
});

const onImageEditorClicked = () => {
  store.dispatch('app/discardActiveObject');
  $XJCanvas && $XJCanvas.renderAll();
};

// ----------------------- start ----------------------------------

const initWithTranslationData = (imageItem) => {
  const { editorSize, getImage } = useSize({ appState });
  watch(editorSize, nv => {
    store.commit('app/SET_SIZE', { ...nv });
  });

  // const imageUrl = getCORSImageUrl(appState.value.RmUrl);
  // 获取图片mime类型
  // apis.app.getImageMimeType(imageUrl).then(res => {
  //   store.commit('app/SET_STATE', { imageExtenssion: res });
  // });
  getImage(appState.value.RmUrl)
    .then(imgInfo => {
      try {
        $XJCanvas && $XJCanvas.dispose();
      } catch (err) { }
      $XJCanvas = new fabric.Canvas('xj-canvas', {});
      editorCore($XJCanvas, store);
      $XJCanvas.setDimensions({ width: imgInfo.width, height: imgInfo.height });
      useController();

      // 背景图发生变化的时候，重新设置背景图
      watch(
        () => appState.value.RmUrl,
        (nv, ov) => {
          if (nv === ov) return;
          if (!$XJCanvas) return;
          $XJCanvas.setBackgroundImage(
            nv,
            () => {
              store.commit('app/SET_EDITOR_LOADING', false);
              store.commit('app/SET_EDITOR_IMAGE_LOADING', false);
              $XJCanvas.renderAll();
            },
            { originX: 'left', originY: 'top', crossOrigin: 'anonymous' }
          );
        },
        { immediate: true }
      );

      initCenteringGuidelines($XJCanvas);
      initAligningGuidelines($XJCanvas);
      $XJCanvas.on('mouse:down', e => {
        console.log('mouse:down');
        if (!e.target) {
          store.dispatch('app/discardActiveObject');
        } else {
          const groupingState = getGroupingObjects();
          const isGrouping = groupingState.objects.length > 1;
          console.log('mouse:down', { isGrouping });
          // 存在组的时候，监听组的移动
          if (isGrouping) {
            store.dispatch('app/setCurrentPropsEditorType', 'group');
            store.dispatch('app/setGroupState', isGrouping);
            try {
              const { group } = groupingState;
              group.setControlVisible('ml', false);
              group.setControlVisible('mr', false);
              group.setControlVisible('mt', false);
              group.setControlVisible('mb', false);
            } catch (err) { }

            const mousemove = debounce(e => {
              console.log('mouse:move', e);
              store.dispatch('app/updateGroupingObjectFormCanvas', groupingState);
            }, 100);

            const clearMouseEvents = debounce(e => {
              console.log(e.target);
              $XJCanvas.off('mouse:move', mousemove);
              $XJCanvas.off('mouse:up', clearMouseEvents);
            });

            $XJCanvas.on('mouse:move', mousemove);
            $XJCanvas.on('mouse:up', clearMouseEvents);
          }
        }
      });

      $XJCanvas.on('selection:created', e => {
        console.log('selection:created', e);
        // 选中的项大于1个时，才算作group
        if (e.selected.length > 1) {
          store.dispatch('app/setGroupState', true);
          console.log('group created');
        }
      });

      $XJCanvas.on('mouse:out', onMouseOut);
      $XJCanvas.on('mouse:move', onCanvasMouseMove);
      $XJCanvas.on('mouse:up', onCanvasMouseUp);

      store.dispatch('app/firstRender', {
        objectPropsMap: {},
        objectUIDs: [],
        cropInfo: {},
        FROM_STORAGE: false
      });
      store.commit('multi/SET_STATE', { againRequestId: '' });
      watch(
        () => multiState.value.textHidden,
        nv => {
          $XJCanvas.getObjects().forEach(i => {
            if (i.type === 'textbox') {
              i.visible = !nv;
            }
          });
          store.dispatch('app/discardActiveObject');
          $XJCanvas.renderAll();
        }
      );
    })
    // 图片加载失败后不影响主流程
    .catch(err => {
      console.error(12345, err);
      $XJCanvas && $XJCanvas.dispose();
      store.commit('app/SET_LOAD_DATA_ERROR', err);
      store.commit('app/SET_EDITOR_LOADING', false);
      store.commit('multi/SET_STATE', { againRequestId: '' });
      store.commit('app/SET_STATE', { OriginUrl: '' });
    });
}

const initBlankCanvas = () => {
  const { editorSize, getImage } = useSize({ appState });
  // 如果检测到空白图片，简化渲染流程
  console.log('=== [空白图片快速渲染] 跳过对象处理，直接加载背景 ===');

  // 使用与正常图片相同的画布初始化流程
  // const imageUrl = getCORSImageUrl(appState.value.RmUrl);

  // 获取图片mime类型（可选）
  // apis.app.getImageMimeType(imageUrl).then(res => {
  //   store.commit('app/SET_STATE', { imageExtenssion: res });
  // });

  getImage(appState.value.RmUrl)
    .then(imgInfo => {
      // Canvas初始化（与正常图片相同）
      debugger
      console.log('=== [空白图片初始化] ===', imgInfo);
      try {
        $XJCanvas && $XJCanvas.dispose();
      } catch (err) { }
      $XJCanvas = new fabric.Canvas('xj-canvas', {});
      editorCore($XJCanvas, store);
      $XJCanvas.setDimensions({ width: imgInfo.width, height: imgInfo.height });
      useController();

      // 直接渲染空白画布，无OCR对象
      store.dispatch('app/firstRender', {
        objectPropsMap: {},
        objectUIDs: [],
        cropInfo: {},
        FROM_STORAGE: false
      });

      // 清理状态
      // store.commit('app/SET_FROM_STORAGE', false);
      store.commit('app/SET_EDITOR_LOADING', false);
      store.commit('app/SET_EDITOR_IMAGE_LOADING', false);
      // store.commit('app/discardActiveObject');
    })
    .catch(err => {
      console.error('=== [空白图片错误] ===', err);
      store.commit('app/SET_LOAD_DATA_ERROR', err);
      store.commit('app/SET_EDITOR_LOADING', false);
    });

  return; // 完成空白图片处理，跳过普通流程
}

let currentInitToken = null;
let stopRmUrlWatch = null; // 用于停止旧的 RmUrl watcher
async function initWithImageItem(imageItem) {
  // debugger
  if (!imageItem) return;
  const { editorSize, getImage } = useSize({ appState });

  // 并发保护：生成当前 token
  const token = (currentInitToken = Symbol());

  try {
    // 进入加载态
    store.commit('app/SET_EDITOR_LOADING', true);
    store.commit('app/SET_LOAD_DATA_ERROR', false);

    // 重置编辑器基础状态
    store.commit('app/RESET_UID');
    store.commit('app/SET_INPAINTING', false);

    // 设置编辑器元数据，确保后续 getImage 使用正确 URL
    store.commit('app/SET_EDITOR_METADATA', {
      Url: imageItem.Url || imageItem.OriginUrl || '',
      OriginUrl: imageItem.OriginUrl || imageItem.Url || '',
      SslRmUrl: imageItem.SslUrl || imageItem.OriginUrl || '',
      RmUrl: imageItem.OriginUrl || imageItem.SslUrl || '',
      WatermarkLayerUrl: imageItem.WatermarkLayerUrl || '',
      HasWatermark: !!imageItem.HasWatermark,
      OriginUrlRemote: imageItem.OriginUrl || imageItem.SslUrl || ''
    });

    // 如果有翻译数据，使用翻译数据初始化（包含OCR对象）
    await initWithTranslationData(imageItem);
    // if (imageItem.translatedData) {
    //   await initWithTranslationData(imageItem);
    // } else {
    //   // 无翻译数据，空白Canvas初始化（整图作为背景）
    //   await initBlankCanvas(imageItem);
    // }
    watch(editorSize, nv => {
      store.commit('app/SET_SIZE', { ...nv });
    });

    // 并发保护：只有当 token 仍是当前 token 才更新状态
    if (token !== currentInitToken) return;

  } catch (error) {
    console.error('=== [ImageEditor] 初始化失败 ===', error);
    store.commit('app/SET_LOAD_DATA_ERROR', error && error.message ? error.message : String(error));
    throw error;
  } finally {
    // 只有当 token 仍是当前 token 才关闭 loading（避免覆盖新 init）
    if (token === currentInitToken) {
      store.commit('app/SET_EDITOR_LOADING', false);
    }
  }
}

async function init() {
  if (!import.meta.env.DEV) {
    await Promise.all([
      store.dispatch('user/getUserInfo'),
      store.dispatch('user/getFontList', { Token: route.query.Token }),
    ]);
  }
  store.commit('multi/SET_HIDE_TEXT', false);
  // 新增：如果是从 URLs 初始化，直接用 multi 的 imageList（或 editImageList）初始化
  // const fromUrls = store.state.multi.initFromUrls;
  // if (fromUrls) {
  const currentRequestId = store.state.multi.requestId;
  const imageItem =
    (store.state.multi.editImageList || []).find(i => i.requestId === currentRequestId) ||
    (store.state.multi.sourceImageList || []).find(i => i.requestId === currentRequestId) ||
    (store.state.multi.imageList || []).find(i => i.requestId === currentRequestId);
  if (imageItem) {
    console.log({ 'imageItem---init': imageItem })
    await initWithImageItem(imageItem);
    // 清除标志，避免重复短路
    store.commit('multi/SET_STATE', { initFromUrls: false });
    return;
  }
  // fallback - 继续走原有 API 流程（如果需要）
  // }

  return

  // ------------------- end --------------------------
  store.dispatch('app/getTranslateResult', { RequestId: requestId.value }).then(res => {
    if (!res) return;
    debugger
    const { objectPropsMap, objectUIDs, FROM_STORAGE, cropInfo, isBlank } = res;
    const { editorSize, getImage } = useSize({ appState });

    // 新增：空白图片快速渲染优化
    console.log('=== [ImageEditor] 处理结果 ===', {
      isBlank: isBlank,
      objectCount: Object.keys(objectPropsMap || {}).length,
      hasStorage: FROM_STORAGE
    });

    // 如果检测到空白图片，简化渲染流程
    if (isBlank) {
      console.log('=== [空白图片快速渲染] 跳过对象处理，直接加载背景 ===');

      // 使用与正常图片相同的画布初始化流程
      const imageUrl = getCORSImageUrl(appState.value.RmUrl);

      // 获取图片mime类型（可选）
      apis.app.getImageMimeType(imageUrl).then(res => {
        store.commit('app/SET_STATE', { imageExtenssion: res });
      });

      getImage(appState.value.RmUrl)
        .then(imgInfo => {
          // Canvas初始化（与正常图片相同）
          console.log('=== [空白图片初始化] ===', imgInfo);
          try {
            $XJCanvas && $XJCanvas.dispose();
          } catch (err) { }
          $XJCanvas = new fabric.Canvas('xj-canvas', {});
          editorCore($XJCanvas, store);
          $XJCanvas.setDimensions({ width: imgInfo.width, height: imgInfo.height });
          useController();

          // 直接渲染空白画布，无OCR对象
          store.dispatch('app/firstRender', {
            objectPropsMap: {},
            objectUIDs: [],
            cropInfo: {},
            FROM_STORAGE: false
          });

          // 清理状态
          store.commit('app/SET_FROM_STORAGE', false);
          store.commit('app/SET_EDITOR_LOADING', false);
          store.commit('app/discardActiveObject');
        })
        .catch(err => {
          console.error('=== [空白图片错误] ===', err);
          store.commit('app/SET_LOAD_DATA_ERROR', err);
          store.commit('app/SET_EDITOR_LOADING', false);
        });

      return; // 完成空白图片处理，跳过普通流程
    }

    watch(editorSize, nv => {
      store.commit('app/SET_SIZE', { ...nv });
    });

    const imageUrl = getCORSImageUrl(appState.value.RmUrl);
    // 获取图片mime类型
    apis.app.getImageMimeType(imageUrl).then(res => {
      store.commit('app/SET_STATE', { imageExtenssion: res });
    });
    getImage(appState.value.RmUrl)
      .then(imgInfo => {
        // canvas横空出世
        try {
          $XJCanvas && $XJCanvas.dispose();
        } catch (err) { }
        $XJCanvas = new fabric.Canvas('xj-canvas', {});
        editorCore($XJCanvas, store);
        $XJCanvas.setDimensions({ width: imgInfo.width, height: imgInfo.height });
        useController();

        // 背景图发生变化的时候，重新设置背景图
        watch(
          () => appState.value.RmUrl,
          (nv, ov) => {
            if (nv === ov) return;
            if (!$XJCanvas) return;
            $XJCanvas.setBackgroundImage(
              getCORSImageUrl(nv),
              () => {
                store.commit('app/SET_EDITOR_LOADING', false);
                store.commit('app/SET_EDITOR_IMAGE_LOADING', false);
                $XJCanvas.renderAll();
              },
              { originX: 'left', originY: 'top', crossOrigin: 'anonymous' }
            );
          },
          { immediate: true }
        );

        initCenteringGuidelines($XJCanvas);
        initAligningGuidelines($XJCanvas);
        $XJCanvas.on('mouse:down', e => {
          console.log('mouse:down');
          if (!e.target) {
            store.dispatch('app/discardActiveObject');
          } else {
            const groupingState = getGroupingObjects();
            const isGrouping = groupingState.objects.length > 1;
            console.log('mouse:down', { isGrouping });
            // 存在组的时候，监听组的移动
            if (isGrouping) {
              store.dispatch('app/setCurrentPropsEditorType', 'group');
              store.dispatch('app/setGroupState', isGrouping);
              try {
                const { group } = groupingState;
                group.setControlVisible('ml', false);
                group.setControlVisible('mr', false);
                group.setControlVisible('mt', false);
                group.setControlVisible('mb', false);
              } catch (err) { }

              const mousemove = debounce(e => {
                console.log('mouse:move', e);
                store.dispatch('app/updateGroupingObjectFormCanvas', groupingState);
              }, 100);

              const clearMouseEvents = debounce(e => {
                console.log(e.target);
                $XJCanvas.off('mouse:move', mousemove);
                $XJCanvas.off('mouse:up', clearMouseEvents);
              });

              $XJCanvas.on('mouse:move', mousemove);
              $XJCanvas.on('mouse:up', clearMouseEvents);
            }
          }
        });

        $XJCanvas.on('selection:created', e => {
          console.log('selection:created', e);
          // 选中的项大于1个时，才算作group
          if (e.selected.length > 1) {
            store.dispatch('app/setGroupState', true);
            console.log('group created');
          }
        });

        $XJCanvas.on('mouse:out', onMouseOut);
        $XJCanvas.on('mouse:move', onCanvasMouseMove);
        $XJCanvas.on('mouse:up', onCanvasMouseUp);

        store.dispatch('app/firstRender', { objectPropsMap, objectUIDs, cropInfo, FROM_STORAGE });
        store.commit('multi/SET_STATE', { againRequestId: '' });
        watch(
          () => multiState.value.textHidden,
          nv => {
            $XJCanvas.getObjects().forEach(i => {
              if (i.type === 'textbox') {
                i.visible = !nv;
              }
            });
            store.dispatch('app/discardActiveObject');
            $XJCanvas.renderAll();
          }
        );
      })
      // 图片加载失败后不影响主流程
      .catch(err => {
        console.error(12345, err);
        $XJCanvas && $XJCanvas.dispose();
        store.commit('app/SET_LOAD_DATA_ERROR', err);
        store.commit('app/SET_EDITOR_LOADING', false);
        store.commit('multi/SET_STATE', { againRequestId: '' });
        store.commit('app/SET_STATE', { OriginUrl: '' });
      });
  });
}

useKeyboard({
  beforeKeydown: () => {
    // 当前有选中的object
    // & 没有激活状态的输入框
    return appState.value.selectedObjectUid > -1 && activeElementNotInputable();
  },
  goDown: () => {
    store.dispatch('app/updateSelectedObjectFromPropsKeyboard', { top: selectedObjectProps.value.top + 1 });
  },
  goUp: () => {
    store.dispatch('app/updateSelectedObjectFromPropsKeyboard', { top: selectedObjectProps.value.top - 1 });
  },
  goLeft: () => {
    store.dispatch('app/updateSelectedObjectFromPropsKeyboard', { left: selectedObjectProps.value.left - 1 });
  },
  goRight: () => {
    store.dispatch('app/updateSelectedObjectFromPropsKeyboard', { left: selectedObjectProps.value.left + 1 });
  },
  doCopy() {
    store.commit('app/SET_COPY_UID');
    console.log('doCopy');
  },
  doPaste() {
    const { type } = appState.value.objectMap[appState.value.copyUID];
    console.log('doPaste', type);
    switch (type) {
      case 'textbox':
        store.dispatch('app/setPasteText');
        break;
      case 'rect':
        store.dispatch('app/setPasteRect');
        break;
    }
  },
  doDel: () => {
    if (appState.value.isGrouping) {
      store.dispatch('app/deleteGroupObject');
      sendLog('deleteGroup');
    } else {
      store.dispatch('app/deleteSelectedObject');
      sendLog('deleteObject');
    }
  },
  undo: () => {
    store.dispatch('record/undo');
  },
  redo: () => {
    store.dispatch('record/redo');
  },
  selectAll: () => {
    // store.dispatch('app/selectAll');
  },
  increaseBrushWidth() {
    const {
      drawingConfig: { brushWidth },
      isDrawingMode,
    } = appState.value;
    isDrawingMode && store.commit('app/SET_DRAWING_CONFIG', { brushWidth: brushWidth >= 100 ? 100 : brushWidth + 1 });
  },
  minusBrushWidth() {
    const {
      drawingConfig: { brushWidth },
      isDrawingMode,
    } = appState.value;
    isDrawingMode && store.commit('app/SET_DRAWING_CONFIG', { brushWidth: brushWidth <= 1 ? 1 : brushWidth - 1 });
  },
  exitDrawingMode() {
    const { isDrawingMode } = appState.value;
    if (isDrawingMode) {
      store.commit('app/SET_DRAWING_MODE', false);
      store.commit('app/SET_STATE', { isInpaintMode: false, isDraw2CancelTransMode: false });
    }
  },
});
</script>
<style lang="less" scoped>
.spinning {
  position: absolute;
  left: 0;
  top: 0;
  font-size: 30px;

  ::v-deep(.ant-spin-dot) {
    font-size: inherit;
  }
}

.image-editor {
  position: relative;
}

.inpaint-mask {
  position: absolute;
  left: 0;
  top: 0;
}

.grab-placeholder {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  cursor: grab;

  &.grabing {
    cursor: grabbing;
  }
}

.xj-canvas {
  transform-origin: 0 0;
}

.xj-canvas,
.orignal-image,
.server-image {
  box-shadow: 1px 1px 15px rgb(0 0 0 / 20%);
}

.pointer-canvas {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  border: 2px solid #000;

  &.inpaint-mode {
    background: transparent !important;

    &:before,
    &::after {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      content: '';
      width: 50%;
      height: 2px;
      background: #000;
    }

    &::after {
      transform: translate(-50%, -50%) rotate(90deg);
    }
  }
}
</style>
